import { Box, LinearProgress, makeStyles, Typography } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { formatCurrency } from './CostDetail'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    container: {
        width: '50%',
        padding: 16,
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[300]}`,
        [theme.breakpoints.down('sm')]: {
            width: '100%'
        }
    },
    progressBar: {
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.palette.grey[300],
        marginTop: 8,
        marginBottom: 8
    },
    progressBarFill: {
        borderRadius: 5,
        backgroundColor: theme.palette.primary.main
    },
    progressBarComplete: {
        borderRadius: 5,
        backgroundColor: theme.palette.success.main
    },
    messageContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    successIcon: {
        color: theme.palette.success.main
    },
    remainingText: {
        color: theme.palette.text.secondary,
        fontWeight: 500
    },
    successText: {
        color: theme.palette.success.main,
        fontWeight: 600
    }
}))

type ValueGapIndicatorProps = {
    returnAmount: number
    exchangeAmount: number
    currency?: string
}

const ValueGapIndicator: React.FC<ValueGapIndicatorProps> = ({ returnAmount, exchangeAmount, currency = 'EUR' }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const gap = returnAmount - exchangeAmount
    const isComplete = gap <= 0
    const progress = returnAmount > 0 ? Math.min((exchangeAmount / returnAmount) * 100, 100) : 0

    return (
        <Box className={classes.wrapper}>
            <Box className={classes.container}>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    className={classes.progressBar}
                    classes={{
                        bar: isComplete ? classes.progressBarComplete : classes.progressBarFill
                    }}
                />
                <Box className={classes.messageContainer}>
                    {isComplete ? (
                        <>
                            <CheckCircleIcon className={classes.successIcon} />
                            <Typography className={classes.successText}>
                                {t('value_gap_reached')}
                            </Typography>
                        </>
                    ) : (
                        <Typography className={classes.remainingText}>
                            {t('value_gap_remaining', { amount: `${currency} ${formatCurrency(gap)}` })}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default ValueGapIndicator
